import {ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {combineLatest, firstValueFrom, Observable, Subscription} from 'rxjs';
import {LearnerProfileResponseDto} from '../../types/LearnerProfileResponseDto';
import {FormControl, FormGroup} from '@angular/forms';
import {filter, map, take} from 'rxjs/operators';
import {ProfilePseudonymService} from './profile-pseudonym.service';
import {PseudonymSettings} from './PseudonymSettings';
import {LearningChannel, LearningChannelType} from './LearningChannel';
import {TranslateService} from '@ngx-translate/core';
import {SelectOption} from '../select/SelectOption';
import {
    UpdateLearnerProfileMainChannelRequestDto
} from '../../services/rest/api-types/UpdateLearnerProfileMainChannelRequestDto';
import {UpdateProfileLocaleRequestDto} from '../../services/rest/api-types/UpdateProfileLocaleRequestDto';
import {UpdateLearningReminderRequestDto} from '../../services/rest/api-types/UpdateLearningReminderRequestDto';
import {LearnerLearningReminderFrequency} from '../../types/LearnerLearningReminderFrequency';
import { ProfileLearningReminderFormGroup } from './ProfileLearningReminderFormGroup';
import {ProfileLocaleFormGroup} from './ProfileLocaleFormGroup';

@Component({
    selector: 'mm-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss'],
    standalone: false
})
export class ProfileComponent implements OnInit, OnDestroy {
    @Input({required: true})
    public profileData!: Observable<LearnerProfileResponseDto>;

    @Input({required: true})
    public locale!: Observable<string>;

    @Input({required: true})
    public darkMode!: boolean;

    @Input()
    public openAsCard: boolean = true;

    @Input()
    public doNotAllowToEdit: boolean = false;

    @Input()
    public shouldTriggerChangeDetectionManually: boolean = false;

    @Input({required: true})
    public profileUpdated!: Observable<void>;

    @Input({required: true})
    public mainChannel$!: Observable<LearningChannelType>;

    @Input({required: true})
    public allChannels$!: Observable<LearningChannelType[]>;

    @Output()
    public generatePseudonym = new EventEmitter();

    @Output()
    public savePseudonymSettings = new EventEmitter<PseudonymSettings>();
    @Output() changedMonthlyReport = new EventEmitter<boolean>();

    @Output()
    public saveLocal = new EventEmitter<UpdateProfileLocaleRequestDto>();

    @Output()
    public saveMainChannel = new EventEmitter<UpdateLearnerProfileMainChannelRequestDto>();

    @Output() saveLearningReminderSettings = new EventEmitter<UpdateLearningReminderRequestDto>();

    public loadedLocale: string | undefined;

    public localeEditorActive: boolean = false;
    public mainChannelEditorActive: boolean = false;
    public pseudonymEditorActive: boolean = false;
    public learningReminderEditorActive: boolean = false;
    public learningReminderFormData = new FormGroup<ProfileLearningReminderFormGroup>({
        learningReminder: new FormControl<boolean | undefined>(true, {nonNullable: true}),
        learningReminderFrequency: new FormControl<LearnerLearningReminderFrequency | undefined>(LearnerLearningReminderFrequency.Biweekly, {nonNullable: true})
    });
    public learningReminderValueChanged: boolean = false;
    public learningReminderFrequencyOptions: SelectOption[] = Object.values(LearnerLearningReminderFrequency).map(data =>
        new SelectOption(data, this.translate.instant(`profile.learningReminderFrequency_${data}`)));
    public usePseudonymFormControl = new FormControl<boolean>(false, {nonNullable: true});
    public useMonthlyReportFormControl = new FormControl<boolean>(true, {nonNullable: true});
    public mainChannelFormControl = new FormControl<string>('', {nonNullable: true});

    public pseudonym: string | undefined;
    private profileDataSubscription = new Subscription();
    private localeSubscription = new Subscription();
    private subscriptionGeneratingPseudonym = new Subscription();
    public pseudonymValueChange: boolean = false;
    public localeValueChanged: boolean = false;
    private initialLocalValue: string | undefined;
    public otherChannels$!: Observable<LearningChannelType[]>;
    public allChannelsOptions$!: Observable<SelectOption[]>;
    private initialMainChannel: string | undefined;
    public mainChannelValueChanged: boolean = false;
    private mainChannelSubscription = new Subscription();
    private mainChannelFormControlChangeSubscription = new Subscription();

    public localeFormData = new FormGroup<ProfileLocaleFormGroup>({
        locale: new FormControl<string>('', {nonNullable: true})
    });

    constructor(private ref: ChangeDetectorRef,
                public profilePseudonymService: ProfilePseudonymService,
                private translate: TranslateService) {
    }

    public ngOnInit(): void {
        if (this.doNotAllowToEdit) {
            this.useMonthlyReportFormControl.disable();
        }
        this.profileDataSubscription = this.profileData.subscribe(profileData => {
            if (profileData !== undefined) {
                this.usePseudonymFormControl.setValue(profileData.usePseudonym);
                this.useMonthlyReportFormControl.setValue(profileData.sendMonthlyLearningReport);
                this.pseudonym = profileData.pseudonym;
                this.learningReminderFormData.controls.learningReminder.setValue(profileData.learningReminder);
                this.learningReminderFormData.controls.learningReminderFrequency.setValue(profileData.learningReminderFrequency);
                this.detectChanges();

                this.usePseudonymFormControl.valueChanges.subscribe(() => {
                    this.pseudonymValueChange = true;
                    this.detectChanges();
                });

                this.learningReminderFormData.valueChanges.subscribe(() => {
                    this.learningReminderValueChanged = true;
                    this.detectChanges();
                });

                this.subscribeGeneratingPseudonym();
            }
        });

        this.localeSubscription = this.locale.subscribe(locale => {
            this.localeFormData.controls.locale.setValue(locale);
            this.initialLocalValue = locale;
            this.loadedLocale = locale;

            this.detectChanges();

            this.localeFormData.valueChanges.subscribe(() => {
                this.localeValueChanged = this.initialLocalValue !== this.localeFormData.controls.locale.getRawValue();
                this.detectChanges();
            });
        });
        void this.setProperChannels();
    }

    private setProperChannels(): void {
        this.otherChannels$ = combineLatest([this.mainChannel$, this.allChannels$]).pipe(filter(([mainChannel, allChannels]: [LearningChannelType, LearningChannelType[]]) => mainChannel !== undefined && allChannels !== undefined),
            map(([mainChannel, allChannels]: [LearningChannelType, LearningChannelType[]]) => allChannels.filter(channel => channel !== mainChannel)));
        this.allChannelsOptions$ = combineLatest([this.mainChannel$, this.allChannels$]).pipe(
            filter(([mainChannel, allChannels]: [LearningChannelType, LearningChannelType[]]) => mainChannel !== undefined && allChannels !== undefined),
            map(([mainChannel, allChannels]: [LearningChannelType, LearningChannelType[]]) => allChannels.map(channel => new SelectOption(channel, this.getChannelName(channel)))
                .sort((a, b) => this.sortChannelsByMain(a, mainChannel, b))));
        this.mainChannelFormControlChangeSubscription = this.mainChannelFormControl.valueChanges.subscribe(() => {
            this.mainChannelValueChanged = this.initialMainChannel !== this.mainChannelFormControl.getRawValue();
            this.detectChanges();
        });
    }

    private sortChannelsByMain(a: SelectOption, mainChannel: LearningChannelType, b: SelectOption): number {
        if (a.value === mainChannel && b.value !== mainChannel) {
            return -1;
        }
        if (a.value !== mainChannel && b.value === mainChannel) {
            return 1;
        }
        return a.label.localeCompare(b.label);
    }

    private subscribeGeneratingPseudonym(): void {
        this.subscriptionGeneratingPseudonym = this.profilePseudonymService.generatingPseudonym$.subscribe(status => {
            const newPseudonym = this.profilePseudonymService.getNewPseudonym();
            if (status === false && newPseudonym !== undefined) {
                this.pseudonym = newPseudonym;
                this.pseudonymValueChange = true;
                this.detectChanges();
            }
        });
    }

    public ngOnDestroy(): void {
        this.profileDataSubscription.unsubscribe();
        this.subscriptionGeneratingPseudonym.unsubscribe();
        this.localeSubscription.unsubscribe();
        this.mainChannelSubscription.unsubscribe();
        this.mainChannelFormControlChangeSubscription.unsubscribe();
        this.cancelProfilePseudonymEditor();
    }

    public openPseudonymEditor(): void {
        if (!this.pseudonymEditorActive && !this.doNotAllowToEdit) {
            this.pseudonymEditorActive = true;
            this.detectChanges();
        }
    }

    public openLocaleEditor(): void {
        if (!this.localeEditorActive && !this.doNotAllowToEdit) {
            this.localeEditorActive = true;
            this.detectChanges();
        }
    }

    public async openMainChannelEditor(): Promise<void> {
        if (!this.mainChannelEditorActive && !this.doNotAllowToEdit) {
            this.initialMainChannel = await firstValueFrom(this.mainChannel$);
            this.mainChannelFormControl.setValue(this.initialMainChannel);
            this.mainChannelEditorActive = true;
            this.detectChanges();
        }
    }

    private detectChanges(): void {
        this.ref.markForCheck();
        if (this.shouldTriggerChangeDetectionManually) {
            this.ref.detectChanges();
        }
    }

    public cancelLocale(): void {
        this.resetLocaleEditor();
        this.detectChanges();
    }

    public cancelPseudonym(): void {
        this.cancelProfilePseudonymEditor();
        this.detectChanges();
    }

    private cancelProfilePseudonymEditor(): void {
        this.profilePseudonymService.cancelPseudonymGeneration();
        this.resetPseudonymEditor();
    }

    public saveLocale: () => Promise<void> = async () => {
        this.localeFormData.disable();
        return new Promise<void>(resolve => {
            this.profileData.pipe(take(1)).subscribe(profileData => {
                const locale = this.localeFormData.controls.locale.getRawValue();
                profileData.locale = locale;
                this.profileUpdated.pipe(take(1)).subscribe(() => {
                    this.resetLocaleEditor();
                    this.detectChanges();

                    // Set a timeout before resolvign slightly improves the flickering
                    // as it gives the browser the option to execute the close of the edit area
                    setTimeout(() => {
                        resolve();
                    });
                });
                this.saveLocal.emit(new UpdateProfileLocaleRequestDto(locale));
            });
        });
    };

    public savePseudonym(): void {
        const pseudonymSettings = new PseudonymSettings(this.usePseudonymFormControl.getRawValue(), this.pseudonym!);
        this.savePseudonymSettings.emit(pseudonymSettings);
        this.resetPseudonymEditor();
    }

    private resetPseudonymEditor(): void {
        this.pseudonymEditorActive = false;
        this.pseudonymValueChange = false;
        if (this.pseudonym !== undefined) {
            this.profileData.pipe(take(1)).subscribe(profileData => {
                this.usePseudonymFormControl.setValue(profileData.usePseudonym);
                this.pseudonym = profileData.pseudonym;
            });
        }
        this.profilePseudonymService.reset();
    }

    private resetLocaleEditor(): void {
        this.localeEditorActive = false;
        this.localeValueChanged = false;
        this.localeFormData.enable();
    }

    public generateNewPseudonym(): void {
        this.profilePseudonymService.startPseudonymGeneration();
        this.detectChanges();
        this.generatePseudonym.emit();
    }

    public openLearningReminderEditor(): void {
        if (!this.learningReminderEditorActive && !this.doNotAllowToEdit) {
            const currentFrequency = this.learningReminderFormData.controls.learningReminderFrequency.getRawValue();
            this.learningReminderFrequencyOptions = [...this.learningReminderFrequencyOptions].sort( (a, b) => (b.value === currentFrequency ? 1 : a.value === currentFrequency ? -1 : 0));
            this.learningReminderEditorActive = true;
            this.detectChanges();
        }
    }

    public cancelLearningReminder(): void {
        this.resetLearningReminderEditor();
        this.detectChanges();
    }

    public saveLearningReminder(): void {
        this.saveLearningReminderSettings.emit(new UpdateLearningReminderRequestDto(
            this.learningReminderFormData.controls.learningReminder.getRawValue(),
            this.learningReminderFormData.controls.learningReminderFrequency.getRawValue()
        ));
        this.resetLearningReminderEditor();
    }

    private resetLearningReminderEditor(): void {
        this.learningReminderEditorActive = false;
        this.learningReminderValueChanged = false;
        this.profileData.pipe(take(1)).subscribe(profileData => {
            this.learningReminderFormData.controls.learningReminder.setValue(profileData.learningReminder);
            this.learningReminderFormData.controls.learningReminderFrequency.setValue(profileData.learningReminderFrequency);
        });
    }

    public changeMonthlyReport(): void {
        if (!this.doNotAllowToEdit) {
            this.changedMonthlyReport.emit(this.useMonthlyReportFormControl.getRawValue());
        }
    }

    private hasMultipleTeamsChannels(): boolean {
        let channels: LearningChannelType[] = [];
        this.allChannels$.pipe(take(1)).subscribe(allChannels => {
            channels = allChannels ?? [];
        });

        const hasMicromate = channels.some(ch => ch === LearningChannel.MicrosoftTeamsMicromate);
        const hasTaskbase = channels.some(ch => ch === LearningChannel.MicrosoftTeamsTaskbase);

        return hasMicromate && hasTaskbase;
    }

    public getChannelName(channel: LearningChannelType): string {
        const showSpecificTeamsLabels = this.hasMultipleTeamsChannels();

        switch (channel) {
            case LearningChannel.MicrosoftTeamsMicromate:
                if (!showSpecificTeamsLabels) {
                    return this.translate.instant('profile.msTeamsChannel');
                }
                return this.translate.instant('profile.msTeamsMicromateChannel');

            case LearningChannel.MicrosoftTeamsTaskbase:
                if (!showSpecificTeamsLabels) {
                    return this.translate.instant('profile.msTeamsChannel');
                }
                return this.translate.instant('profile.msTeamsTaskbaseChannel');

            case LearningChannel.Learn:
                return this.translate.instant('profile.learnChannel');
            case LearningChannel.WidgetMoodle:
                return this.translate.instant('profile.moodleChannel');
            case LearningChannel.Slack:
                return this.translate.instant('profile.slackChannel');
            case LearningChannel.GoogleChat:
                return this.translate.instant('profile.googleChatChannel');
            default:
                return channel;
        }
    }

    public cancelMainChannel(): void {
        this.resetMainChannelEditor();
        this.detectChanges();
    }

    public saveMainChannelAction: () => Promise<void> = async () => {
        this.mainChannelFormControl.disable();
        return new Promise<void>(resolve => {
            this.profileData.pipe(take(1)).subscribe(profileData => {
                const mainChannel = this.mainChannelFormControl.getRawValue();
                profileData.mainChannel = mainChannel;
                this.profileUpdated.pipe(take(1)).subscribe(() => {
                    this.resetMainChannelEditor();
                    this.detectChanges();

                    // Set a timeout before resolvign slightly improves the flickering
                    // as it gives the browser the option to execute the close of the edit area
                    setTimeout(() => {
                        resolve();
                    });
                });
                this.saveMainChannel.emit(new UpdateLearnerProfileMainChannelRequestDto(mainChannel));
            });
        });
    };

    private resetMainChannelEditor(): void {
        this.mainChannelEditorActive = false;
        this.mainChannelValueChanged = false;
        this.mainChannelFormControl.enable();
    }

}
